import { Box, Icon, Image, Theme, Typography } from "@outschool/backpack";
import { getFundingProgramProperties } from "@outschool/funding-programs-shared";
import { faTimes } from "@outschool/icons";
import { useLocalStorageState } from "@outschool/local-storage";
import { Trans, useTranslation } from "@outschool/localization";
import { joinMembershipPath } from "@outschool/routes";
import { dayjs } from "@outschool/time";
import { useTrackEvent } from "@outschool/ui-analytics";
import { useFundingProgramAbbr, useSession } from "@outschool/ui-auth";
import {
  useIsEligibleFor40PercentCouponUpsell,
  useRequiresMembershipForEnrollment
} from "@outschool/ui-components-website";
import { useIsMobile, useNavigation } from "@outschool/ui-utils";
import React from "react";
import { useLocation } from "react-router";

import blobLeft from "../../../images/banners/MembershipMigrationPromotionalBanner/blob_left.png";
import { isOrganizationAdminWhoCanEnroll } from "../../lib/Users";
import Link from "../Link";

export default function MembershipMigrationPromotionalBanner() {
  const { t } = useTranslation(
    "client\\components\\banners\\MembershipMigrationPromotionalBanner"
  );
  const isMobile = useIsMobile();
  const { currentUser } = useSession();
  const navigate = useNavigation();
  const trackEvent = useTrackEvent();

  const esaState = useFundingProgramAbbr();
  const fundingProgramProperties = getFundingProgramProperties(esaState);
  const isClassWalletProgram = fundingProgramProperties?.isClassWallet;
  const fundingProgramName = fundingProgramProperties?.programName;

  const { requiresMembershipForEnrollment } =
    useRequiresMembershipForEnrollment();
  const { isEligibleFor40PercentCouponUpsell } =
    useIsEligibleFor40PercentCouponUpsell();

  // Track which promotional date has been viewed to prevent duplicate tracking
  const trackedPromotionalDateRef = React.useRef<string | null>(null);

  // Track when the banner was dismissed for the current promotional period
  const [bannerClosedAt, setBannerClosedAt] = useLocalStorageState<
    string | null
  >("Memberships.migration_promotional_banner_closed_at", null);

  const PROMOTIONAL_BANNER_DATA = [
    {
      date: "2025-07-07",
      node: (
        <Trans t={t}>
          <Typography variant="body1" sx={{ textAlign: "center" }}>
            Back to school, your way.{" "}
            <b>Get 40% off your plan plus bonus credits</b>—up to 50% in
            savings. Offer ends soon.
          </Typography>
        </Trans>
      )
    },
    {
      date: "2025-07-14",
      node: (
        <Trans t={t}>
          <Typography variant="body1" sx={{ textAlign: "center" }}>
            Smarter learning starts now. Save up to 50% with{" "}
            <b>40% off your plan + bonus credits</b>. Offer ends soon.
          </Typography>
        </Trans>
      )
    },
    {
      date: "2025-07-21",
      node: (
        <Trans t={t}>
          <Typography variant="body1" sx={{ textAlign: "center" }}>
            Make the most of back-to-school. Up to 50% in savings with
            Membership. Offer ends soon.{" "}
            <Link
              trackingName="membership_migration_promotional_learn_more_click"
              to={joinMembershipPath()}
            >
              Learn more
            </Link>
          </Typography>
        </Trans>
      )
    }
  ];

  const currentDate = dayjs();
  const location = useLocation();

  // Find the current promotional datum based on current date
  const currentPromotionalDatum = [...PROMOTIONAL_BANNER_DATA]
    .reverse()
    .find(datum => currentDate.isSameOrAfter(dayjs(datum.date), "day"));

  const shouldShowBanner = (() => {
    if (!isEligibleFor40PercentCouponUpsell) {
      return false;
    }

    // Don't show on the /join-membership page or teacher pages
    if (
      location.pathname === joinMembershipPath() ||
      location.pathname.includes("/teach/") ||
      location.pathname.includes("/account/teacher")
    ) {
      return false;
    }

    // Don't show the banner after 2025-08-31 in user's timezone
    if (currentDate.isAfter(dayjs("2025-08-31"), "day")) {
      return false;
    }

    if (!currentUser) {
      return false;
    }

    if (
      requiresMembershipForEnrollment ||
      currentUser.canPayWithSubscriptionCredits ||
      isOrganizationAdminWhoCanEnroll(currentUser) ||
      isClassWalletProgram ||
      fundingProgramName
    ) {
      return false;
    }

    if (!currentPromotionalDatum) {
      return false;
    }

    if (!bannerClosedAt) {
      return true; // Never been dismissed, show it
    }

    try {
      const closedDate = dayjs(bannerClosedAt);
      const currentPromotionalDate = dayjs(currentPromotionalDatum.date);

      // Show banner if we're in a new promotional period (current promotional date is after the dismissal date)
      return currentPromotionalDate.isAfter(closedDate, "day");
    } catch (error) {
      // If there's an error parsing the stored date, reset and show the banner
      setBannerClosedAt(null);
      return true;
    }
  })();

  const handleClose = React.useCallback(() => {
    // Store the current date as ISO string for when banner was dismissed
    setBannerClosedAt(dayjs().toISOString());
  }, [setBannerClosedAt]);

  const handleBannerClick = React.useCallback(() => {
    trackEvent("membership_migration_promotional_banner_click", {
      promotional_date: currentPromotionalDatum?.date,
      is_mobile: isMobile
    });
    navigate(joinMembershipPath());
  }, [trackEvent, currentPromotionalDatum?.date, isMobile, navigate]);

  // Track banner view when it's shown
  React.useEffect(() => {
    if (shouldShowBanner && currentPromotionalDatum) {
      const currentDate = currentPromotionalDatum.date;
      if (trackedPromotionalDateRef.current !== currentDate) {
        trackedPromotionalDateRef.current = currentDate;
        trackEvent("membership_migration_promotional_banner_view", {
          promotional_date: currentDate,
          is_mobile: isMobile
        });
      }
    }
  }, [shouldShowBanner, currentPromotionalDatum, trackEvent, isMobile]);

  if (!shouldShowBanner || !currentPromotionalDatum) {
    return null;
  }

  return (
    <Box
      sx={(theme: Theme) => ({
        display: "flex",
        backgroundColor: theme.palette.warning[300],
        minHeight: "56px",
        alignItems: "center",
        cursor: "pointer"
      })}
      onClick={handleBannerClick}
    >
      {!isMobile && <Image src={blobLeft} sx={{ height: 56 }} />}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          flex: 1,
          padding: isMobile ? "1em" : "0px"
        }}
      >
        {currentPromotionalDatum.node}
      </Box>

      <Box
        onClick={(e: React.MouseEvent<HTMLElement>) => {
          // Don't click the parent component
          e.stopPropagation();
          handleClose();
        }}
        sx={{
          flexDirection: "column",
          cursor: "pointer",
          height: "44px",
          width: "44px",
          display: "flex",
          alignItems: "center",
          justifyContent: isMobile ? "flex-start" : "center",
          marginTop: isMobile ? "8px" : "0px",
          marginLeft: isMobile ? "0px" : "61px"
        }}
      >
        <Icon
          aria-label={t("Close")}
          icon={faTimes}
          sx={(theme: Theme) => ({
            color: theme.palette.common.black
          })}
        />
      </Box>
    </Box>
  );
}
